import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Spinner, Alert, Image } from 'react-bootstrap';
import { FaUser, FaPhone, FaEnvelope, FaIdCard, FaCalendarAlt, FaSave, FaTimes, FaUserShield } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserService from '../services/UserService';
import FaylService from '../services/FaylService';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    phone: '',
    email: '',
    prava_raqam: '',
    role: '',
    dob: '',
    password: '',
    password_confirmation: ''
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        // Get current user ID from local storage or context
        const userId = localStorage.getItem('userId');
        
        // Fetch user data
        const [userResponse, rolesResponse] = await Promise.all([
          UserService.getUserById(userId),
          UserService.getUserRoles()
        ]);
        
        if (userResponse.success && userResponse.data) {
          const userData = userResponse.data;
          setUser(userData);
          
          // Set roles from the response
          if (rolesResponse.success && rolesResponse.data) {
            setRoles(rolesResponse.data);
          }
          
          setFormData({
            name: userData.name || '',
            username: userData.username || '',
            phone: userData.phone || '',
            email: userData.email || '',
            prava_raqam: userData.prava_raqam || '',
            role: userData.roles?.[0]?.name || '',
            dob: userData.dob ? userData.dob.split('T')[0] : '',
            password: '',
            password_confirmation: ''
          });
          
          if (userData.avatar) {
            setAvatarPreview(FaylService.getAvatarPath(userData.avatar));
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Профильни юклаб бўлмади. Қайтадан уриниб кўринг.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password if changing
    if (formData.password && formData.password !== formData.password_confirmation) {
      toast.error('Parollar mos kelmadi!');
      return;
    }
    
    try {
      setSaving(true);
      const formDataToSend = new FormData();
      
      // Append user data
      Object.keys(formData).forEach(key => {
        // Only include password fields if they are being changed
        if ((key === 'password' || key === 'password_confirmation') && !formData[key]) {
          return;
        }
        formDataToSend.append(key, formData[key]);
      });
      
      // Append role if changed
      if (formData.role) {
        formDataToSend.append('role', formData.role);
      }
      
      // Append avatar if changed
      if (avatar) {
        formDataToSend.append('avatar', avatar);
      }

      // Update user
      const response = await UserService.updateUser(formDataToSend, user.id);
      
      if (response.success) {
        toast.success('Profil muvaffaqiyatli yangilandi!');
        // Update user data including roles
        setUser(prev => ({
          ...prev,
          ...response.data,
          roles: response.data.roles || prev.roles
        }));
        setEditing(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.message || 'Profilni yangilashda xatolik yuz berdi!');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !user) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Xatolik!</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xl={10} lg={12} xxl={10}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Profil malumotlari</h5>
              {!editing ? (
                <Button variant="light" size="sm" onClick={() => setEditing(true)}>
                  Таҳрирлаш
                </Button>
              ) : (
                <div>
                  <Button variant="light" size="sm" className="me-2" onClick={() => setEditing(false)}>
                    <FaTimes className="me-1" /> Бекор қилиш
                  </Button>
                  <Button variant="success" size="sm" onClick={handleSubmit} disabled={saving}>
                    <FaSave className="me-1" /> Сақлаш
                  </Button>
                </div>
              )}
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <Row className="align-items-center">
                    <Col md="auto" className="text-center">
                      <div className="position-relative d-inline-block">
                        <Image 
                          src={avatarPreview || '/default-avatar.png'} 
                          roundedCircle 
                          width={100} 
                          height={100}
                          className="border border-3 border-primary"
                        />
                        {editing && (
                          <div className="position-absolute bottom-0 end-0">
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="d-none"
                              id="avatar-upload"
                            />
                            <Form.Label 
                              htmlFor="avatar-upload" 
                              className="btn btn-primary btn-sm rounded-circle p-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <FaUser />
                            </Form.Label>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col>
                      <h4 className="mb-1">{formData.name || 'Foydalanuvchi'}</h4>
                      <p className="text-muted mb-0">{formData.role || 'Rol tanlanmagan'}</p>
                    </Col>
                  </Row>
                </div>

                {/* First Row: Name, Phone, Email */}
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>To'liq ismi *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!editing}
                        placeholder="Ism familiya"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Telefon raqami</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!editing}
                        placeholder="+998901234567"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                        disabled={!editing}
                        placeholder="example@mail.com"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Second Row: License, DOB, Role */}
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Haydovchilik guvohnoma raqami</Form.Label>
                      <Form.Control
                        type="text"
                        name="prava_raqam"
                        value={formData.prava_raqam}
                        onChange={handleInputChange}
                        disabled={!editing}
                        placeholder="AA1234567"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Tug'ilgan sana</Form.Label>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        disabled={!editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Rol *</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role || ''}
                        onChange={handleInputChange}
                        disabled={!editing}
                        required
                      >
                        <option value="">Rolni tanlang</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.name}>
                            {role.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mt-4 mb-3">Kirish ma'lumotlari</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Foydalanuvchi nomi</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        disabled={!editing}
                        placeholder="username"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {editing && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Yangi parol</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Parolni kiriting"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parolni tasdiqlash</Form.Label>
                        <Form.Control
                          type="password"
                          name="password_confirmation"
                          value={formData.password_confirmation}
                          onChange={handleInputChange}
                          placeholder="Parolni qayta kiriting"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Alert variant="info" className="small mb-4">
                  Агар паролни ўзгартирмоқчи бўлмасангиз, парол майдонини бўш қолдиринг.
                </Alert>
              </Form>
            </Card.Body>
            <Card.Footer className="text-muted">
              Қўшилган санаси: {new Date(user?.created_at).toLocaleDateString('uz-UZ')}
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile;
